import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DIST_PORTFOLIOS, distLabel } from "./distConfig";
import { buildBlendedPortfolioMatrix } from "@/lib/blendedPortfolioMatrix";
import type { FeBreakdownRow, FeMappingRow, FeSplitOverride } from "@/components/fe/feMatching";

type ViewLabel = "ERROR" | "UNDERWEIGHT" | "NEGATIVE" | "NEUTRAL" | "POSITIVE" | "OVERWEIGHT";

const VIEW_LABEL_STYLES: Record<ViewLabel, string> = {
  ERROR: "bg-red-600 text-white",
  UNDERWEIGHT: "text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950",
  NEGATIVE: "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950",
  NEUTRAL: "text-muted-foreground bg-muted",
  POSITIVE: "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950",
  OVERWEIGHT: "text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900",
};

function computeViewLabel(rel: number): ViewLabel {
  if (rel < -10) return "ERROR";
  if (rel <= -2.5) return "UNDERWEIGHT";
  if (rel < -0.5) return "NEGATIVE";
  if (rel <= 0.5) return "NEUTRAL";
  if (rel <= 2.5) return "POSITIVE";
  if (rel <= 10) return "OVERWEIGHT";
  return "ERROR";
}

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; }
interface SectorMapping { fund_id: string; sector_id: string; weighting: number; }
interface FundHouse { id: string; benchmark: number; }
interface Allocation { benchmark: number; sector_name: string; fund_house_id: string; weight_pct: number; }

export default function DistBenchmarkDashboard() {
  const { data: sectors, refetch: refetchSectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("*").order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const { data: weights, refetch: refetchWeights } = useQuery({
    queryKey: ["ba-dist-fund-weights-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_dist_cbam_fund_weights")
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      const seen = new Set<string>();
      const latest: FundWeight[] = [];
      for (const w of (data as any[])) {
        const key = `${w.fund_id}::${w.portfolio}`;
        if (seen.has(key)) continue;
        seen.add(key);
        latest.push({ fund_id: w.fund_id, portfolio: w.portfolio, weight_pct: Number(w.weight_pct) });
      }
      return latest;
    },
  });

  const { data: mappings, refetch: refetchMappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_fund_sector_mappings").select("fund_id, sector_id, weighting");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: houses, refetch: refetchHouses } = useQuery({
    queryKey: ["bm-dist-fund-houses-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("bm_dist_fund_houses").select("id, benchmark");
      if (error) throw error;
      return data as FundHouse[];
    },
  });

  const { data: allocations, refetch: refetchAllocations } = useQuery({
    queryKey: ["bm-dist-allocations-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("bm_dist_allocations").select("benchmark, sector_name, fund_house_id, weight_pct");
      if (error) throw error;
      return data as Allocation[];
    },
  });

  const { data: cbamFunds } = useQuery({
    queryKey: ["ba-cbam-funds-dist"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, isin").eq("is_current", true);
      if (error) throw error;
      return data as { id: string; isin: string | null }[];
    },
  });

  const { data: feRows } = useQuery({
    queryKey: ["fe-asset-breakdown-latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of (data ?? []) as any[]) {
        if (!r.isin) continue;
        if (!latest.has(r.isin)) latest.set(r.isin, { ...r, breakdown: r.breakdown ?? {} });
      }
      return Array.from(latest.values());
    },
  });

  const { data: feMapping } = useQuery({
    queryKey: ["fe-sector-mapping"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_sector_mapping").select("*").order("display_order");
      if (error) throw error;
      return data as FeMappingRow[];
    },
  });

  const { data: feOverrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return data as FeSplitOverride[];
    },
  });

  // Blended manual + FE matrix (50/50 when both resolve), matches Dist Sector Weightings page.
  const pMatrix =
    weights && mappings && sectors && cbamFunds && feRows && feMapping && feOverrides
      ? buildBlendedPortfolioMatrix({
          weights,
          mappings,
          sectors,
          cbamFunds,
          feRows,
          feMapping,
          feOverrides,
        })
      : new Map<string, number>();

  const sectorIdByName = new Map<string, string>();
  (sectors || []).forEach((s) => sectorIdByName.set(s.name.trim().toLowerCase(), s.id));

  const bmMean = new Map<string, number>();
  if (houses && allocations) {
    const housesByBench = new Map<number, number>();
    houses.forEach((h) => housesByBench.set(h.benchmark, (housesByBench.get(h.benchmark) || 0) + 1));
    const sumMap = new Map<string, number>();
    for (const a of allocations) {
      const sid = sectorIdByName.get((a.sector_name || "").trim().toLowerCase());
      if (!sid) continue;
      const k = `${a.benchmark}-${sid}`;
      sumMap.set(k, (sumMap.get(k) || 0) + Number(a.weight_pct));
    }
    for (const [k, sum] of sumMap.entries()) {
      const bench = parseInt(k.split("-")[0]);
      bmMean.set(k, sum / (housesByBench.get(bench) || 1));
    }
  }

  const relWeight = (p: number, sector: MasterSector) => {
    const pVal = pMatrix.get(`${p}-${sector.id}`) || 0;
    const bmVal = bmMean.get(`${p}-${sector.id}`) || 0;
    return pVal - bmVal;
  };

  const visibleSectors = (sectors || []).filter((sector) => {
    const hasP = DIST_PORTFOLIOS.some((p) => (pMatrix.get(`${p}-${sector.id}`) || 0) !== 0);
    const hasBM = DIST_PORTFOLIOS.some((p) => (bmMean.get(`${p}-${sector.id}`) || 0) !== 0);
    return hasP || hasBM;
  });

  const refreshAll = () => {
    refetchSectors(); refetchWeights(); refetchMappings(); refetchHouses(); refetchAllocations();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dist Portfolio Management</h1>
          <p className="text-muted-foreground text-sm">
            Portfolio Views Matrix — Dist Sector Weightings vs Benchmark Top Level Summary mean
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {visibleSectors.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">
              No data available yet. Add fund holdings, sector mappings, and benchmark fund-house allocations to populate this view.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-lg">Portfolio Views Matrix</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-base">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-muted-foreground min-w-[240px] sticky left-0 bg-muted/50 z-10">
                    CBAM Master Sector
                  </th>
                  {DIST_PORTFOLIOS.map((p) => (
                    <th key={p} className="text-center p-3 font-medium text-muted-foreground min-w-[140px]">{distLabel(p)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleSectors.map((sector, idx) => (
                  <tr key={sector.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className={cn("p-3 font-medium text-sm sticky left-0 z-10", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                      {sector.name}
                    </td>
                    {DIST_PORTFOLIOS.map((p) => {
                      const rel = relWeight(p, sector);
                      const label = computeViewLabel(rel);
                      return (
                        <td key={p} className="p-2 text-center">
                          <div className={cn("rounded-md px-2 py-1.5 text-xs font-medium inline-flex flex-col items-center gap-0.5 min-w-[110px]", VIEW_LABEL_STYLES[label])}>
                            <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
                            <span className="text-[11px] font-mono">{rel > 0 ? "+" : ""}{rel.toFixed(2)}%</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-muted/50 font-semibold">
                  <td className="p-3 text-sm sticky left-0 bg-muted/50 z-10">Total</td>
                  {DIST_PORTFOLIOS.map((p) => {
                    const total = visibleSectors.reduce((sum, s) => sum + relWeight(p, s), 0);
                    return (
                      <td key={p} className="p-2 text-center font-mono text-xs">
                        {total > 0 ? "+" : ""}{total.toFixed(2)}%
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
